import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, PlusCircle, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FormField } from "@/components/pages/reservations/guests/components/form/FormFields";
import ImageSelector from "@/components/pages/reservations/guests/components/form/ImageSelector";
import LanguageSelect from "@/components/common/LanguageSelect";
import {
  useAccountProfile,
  useUpdateProfile,
} from "../hooks/useAccountProfile";
import {
  createAccountProfileSchema,
  AccountProfileFormData,
} from "../utils/accountSchema";
import { fileService } from "@/services/file-service";
import { AccountProfile } from "@/services/accountProfileService";
import { formatDate, formatDateTime } from "@/utils/dateUtils";
import { useAdminImpersonation } from "@/hooks/useAdminImpersonation";
import { PlatformRole } from "@/utils/accessControl";
import { cn } from "@/lib/utils";

interface ReadOnlyFieldProps {
  label: string;
  value: string | React.ReactNode;
}

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value }) => (
  <div className="flex flex-col gap-1">
    <p className="text-muted-foreground text-sm font-medium">{label}</p>
    <p className="text-sm">{value || "-"}</p>
  </div>
);

/**
 * Extracts profile fields from the API response.
 * Handles both nested (.profile) and flat response shapes.
 */
const extractProfileFields = (data?: AccountProfile | null) => {
  if (!data) return null;
  const p = data.profile;
  if (p) return p;

  // Fallback: fields might be at root level
  const anyData = data as unknown as Record<string, unknown>;
  if (anyData.firstName || anyData.email) {
    return {
      firstName: anyData.firstName as string | undefined,
      lastName: anyData.lastName as string | undefined,
      email: anyData.email as string | undefined,
      phone: anyData.phone as string | undefined,
      address: anyData.address as string | undefined,
      language: anyData.language as string | undefined,
      preferredLanguage: (anyData.preferredLanguage as string) || "en",
      hotelId: anyData.hotelId as string | undefined,
      dateOfBirth: anyData.dateOfBirth as string | undefined,
      photoId: anyData.photoId as string | undefined,
    };
  }

  return null;
};

const ACCOUNT_PROFILE_FORM_FIELDS: (keyof AccountProfileFormData)[] = [
  "firstName",
  "lastName",
  "email",
  "phone",
  "address",
  "language",
  "preferredLanguage",
  "photoId",
];

const normalizeFormValue = (value?: string | null) => value ?? "";

export const GeneralTab: React.FC = () => {
  const { data: accountData, isLoading } = useAccountProfile();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
      </div>
    );
  }

  if (!accountData) {
    return null;
  }

  // Build the form only once the profile is loaded, so the schema — which
  // depends on the role — is correct and stable from the first render.
  return <GeneralTabForm accountData={accountData} />;
};

interface GeneralTabFormProps {
  accountData: AccountProfile;
}

const GeneralTabForm: React.FC<GeneralTabFormProps> = ({ accountData }) => {
  const { t } = useTranslation();
  const updateProfile = useUpdateProfile();
  const { isImpersonating } = useAdminImpersonation();
  const [selectedPhotoFile, setSelectedPhotoFile] = useState<File | null>(null);

  const profile = extractProfileFields(accountData);

  // Platform admins (SUPER_ADMIN / GUESTERIA_ADMIN) have no region profile, so
  // the contact fields (phone, address, languages, date of birth) are hidden.
  // The schema is relaxed to match, so only the visible fields (first name, last
  // name, email) are validated — the hidden fields are not required.
  const isPlatformAdmin =
    accountData.role === PlatformRole.SUPER_ADMIN ||
    accountData.role === PlatformRole.GUESTERIA_ADMIN;

  const initialFormValues = useMemo<AccountProfileFormData>(
    () => ({
      firstName: profile?.firstName || "",
      lastName: profile?.lastName || "",
      email: profile?.email || "",
      phone: profile?.phone || "",
      address: profile?.address || "",
      language: profile?.language || "",
      preferredLanguage: profile?.preferredLanguage || "en",
      photoId: profile?.photoId || "",
    }),
    [
      profile?.address,
      profile?.email,
      profile?.firstName,
      profile?.language,
      profile?.lastName,
      profile?.phone,
      profile?.photoId,
      profile?.preferredLanguage,
    ],
  );
  const [baselineValues, setBaselineValues] =
    useState<AccountProfileFormData>(initialFormValues);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
  } = useForm<AccountProfileFormData>({
    resolver: zodResolver(createAccountProfileSchema(!isPlatformAdmin)),
    defaultValues: initialFormValues,
  });

  useEffect(() => {
    setBaselineValues(initialFormValues);
    setSelectedPhotoFile(null);
    reset(initialFormValues);
  }, [initialFormValues, reset]);

  const formValues = watch();
  const selectedImage = formValues.photoId;
  const hasUnsavedChanges =
    selectedPhotoFile !== null ||
    ACCOUNT_PROFILE_FORM_FIELDS.some(
      (field) =>
        normalizeFormValue(formValues[field]) !==
        normalizeFormValue(baselineValues[field]),
    );

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const previousUrl = watch("photoId");
      if (previousUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previousUrl);
      }
      const imageUrl = URL.createObjectURL(file);
      setValue("photoId", imageUrl, { shouldDirty: true });
      setSelectedPhotoFile(file);
    }
  };

  const onSubmit = async (data: AccountProfileFormData) => {
    if (isImpersonating) {
      toast.error(t("account.impersonationBlocked"));
      return;
    }

    let photoId: string | undefined = data.photoId || undefined;

    if (selectedPhotoFile) {
      try {
        photoId =
          await fileService.uploadFileWithPresignedUrl(selectedPhotoFile);
      } catch {
        toast.error(t("account.general.photoUploadError"));
        return;
      }
    }

    await updateProfile.mutateAsync({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      address: data.address || undefined,
      language: data.language || undefined,
      preferredLanguage: data.preferredLanguage || undefined,
      photoId,
    });

    const savedValues = { ...data, photoId: photoId || "" };

    setSelectedPhotoFile(null);
    setBaselineValues(savedValues);
    // Re-baseline the form to the saved values so Save
    // disables again until the next edit.
    reset(savedValues);
  };

  const getRoleLabel = (role?: string): string => {
    if (!role) return "-";
    return t(`layout.roles.${role}`, { defaultValue: role });
  };

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("account.general.personalInfo")}</CardTitle>
          <CardDescription>
            {t("account.general.personalInfoDescription")}
          </CardDescription>
          {isImpersonating ? (
            <p className="text-muted-foreground text-sm">
              {t("account.impersonationBlocked")}
            </p>
          ) : null}
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
          >
            {/* Avatar Upload */}
            <div className="flex justify-center">
              <ImageSelector
                defaultIcon={
                  <UserCircle2 className="text-muted-foreground size-[72px] stroke-1" />
                }
                overlayContent={
                  <div
                    className="bg-background absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full"
                    role="button"
                    aria-label={t("account.general.profilePhoto")}
                  >
                    <PlusCircle className="size-5" />
                  </div>
                }
                onImageSelect={handleImageUpload}
                selectedImage={selectedImage || undefined}
                imageClassName="size-[72px] rounded-full"
                disabled={isImpersonating}
              />
            </div>

            {/* Name */}
            <div className="grid grid-cols-2 gap-6">
              <FormField
                id="firstName"
                label={t("account.general.firstName")}
                required
                register={register}
                error={errors.firstName?.message}
                placeholder={t("account.general.firstName")}
                className="h-12"
                disabled={isImpersonating}
              />
              <FormField
                id="lastName"
                label={t("account.general.lastName")}
                required
                register={register}
                error={errors.lastName?.message}
                placeholder={t("account.general.lastName")}
                className="h-12"
                disabled={isImpersonating}
              />
            </div>

            {/* Email and Phone */}
            <div
              className={cn(
                "grid gap-6",
                isPlatformAdmin ? "grid-cols-1" : "grid-cols-2",
              )}
            >
              <FormField
                id="email"
                label={t("account.general.email")}
                type="email"
                required
                register={register}
                error={errors.email?.message}
                placeholder="email@example.com"
                className="h-12"
                disabled={isImpersonating}
              />
              {!isPlatformAdmin && (
                <FormField
                  id="phone"
                  label={t("account.general.phone")}
                  type="tel"
                  required
                  register={register}
                  error={errors.phone?.message}
                  placeholder="+1 000 000 0000"
                  className="h-12"
                  disabled={isImpersonating}
                />
              )}
            </div>

            {/* Address */}
            {!isPlatformAdmin && (
              <FormField
                id="address"
                label={t("account.general.address")}
                register={register}
                error={errors.address?.message}
                placeholder={t("account.general.address")}
                className="h-12"
                disabled={isImpersonating}
              />
            )}

            {/* Language selects */}
            {!isPlatformAdmin && (
              <div className="grid grid-cols-2 gap-6">
                <LanguageSelect
                  label={t("account.general.language")}
                  value={watch("language")}
                  onChange={(val) =>
                    setValue("language", val, { shouldDirty: true })
                  }
                  className="h-12"
                  triggerClassName="h-12"
                  disabled={isImpersonating}
                />
                <LanguageSelect
                  label={t("account.general.preferredLanguage")}
                  value={watch("preferredLanguage")}
                  onChange={(val) =>
                    setValue("preferredLanguage", val, { shouldDirty: true })
                  }
                  className="h-12"
                  triggerClassName="h-12"
                  disabled={isImpersonating}
                />
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end pt-6">
              <Button
                type="submit"
                variant="new_default"
                size="lg"
                disabled={
                  !hasUnsavedChanges ||
                  isSubmitting ||
                  updateProfile.isPending ||
                  isImpersonating
                }
              >
                {isSubmitting || updateProfile.isPending
                  ? t("account.general.saving")
                  : t("account.general.saveChanges")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Read-only Account Info */}
      <Card>
        <CardHeader>
          <CardTitle>{t("account.general.accountInfo")}</CardTitle>
          <CardDescription>
            {t("account.general.accountInfoDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <ReadOnlyField
              label={t("account.general.role")}
              value={
                <Badge variant="new_secondary">
                  {getRoleLabel(accountData?.role)}
                </Badge>
              }
            />
            <ReadOnlyField
              label={t("account.general.memberSince")}
              value={formatDate(accountData?.createdAt)}
            />
            <ReadOnlyField
              label={t("account.general.lastLogin")}
              value={formatDateTime(accountData?.lastLogin)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
